import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Header, Table, Icon } from 'semantic';
import Flex from 'common/layout/helpers/Flex';
import ConfirmDelete from 'components/ConfirmDelete';
import { request } from 'utils/api';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import ErrorMessage from 'components/ErrorMessage';
import { CustomerGroup } from 'subdomains/cpo/types/charging-policy';
import { EditCustomerGroupModal } from 'subdomains/cpo/screens/policies-and-groups/screens/provider-admin/customer-groups/CustomerGroup/EditCustomerGroupModal';
import { SegmentLoader } from '../../../../../components/SegmentLoader';
import { PAGE_LIMIT } from '../../../../../config/pagination';
import { TablePagination } from '../../../../../components/TablePagination';
import { CUSTOMER_GROUPS_V1_API_URL } from 'subdomains/cpo/router/api';
import { useToast } from 'components/Toast/useToast';

export function CustomerGroups() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const toast = useToast();

  const {
    data: res,
    isPending,
    error,
    refetch,
  } = useQuery<{
    data: CustomerGroup[];
    meta: { total: number; limit: number };
  }>({
    queryKey: [CUSTOMER_GROUPS_V1_API_URL, page],
    queryFn: async () => {
      const { data, meta } = await request<{
        data: CustomerGroup[];
        meta: { total: number; limit: number };
      }>({
        path: CUSTOMER_GROUPS_V1_API_URL,
        method: 'GET',
        params: {
          limit: PAGE_LIMIT,
          offset: (page - 1) * PAGE_LIMIT,
        },
      });

      return { data, meta };
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await request({
        path: `${CUSTOMER_GROUPS_V1_API_URL}/${id}`,
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      refetch();
      toast.success(
        t(
          'providerAdmin.customerGroup.deleteSuccess',
          'Customer group deleted successfully'
        )
      );
    },
    onError: () => {
      toast.error(
        t(
          'providerAdmin.customerGroup.deleteError',
          'Failed to delete customer group'
        )
      );
    },
  });

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const customerGroups = res?.data ?? [];
  const total = res?.meta?.total ?? 0;

  return (
    <>
      <Flex direction="row" justify="space-between" align="center">
        <Header as="h2" style={{ marginTop: '1em' }}>
          {t('providerAdmin.customerGroupsTable.title', 'Customer Groups')}
        </Header>
        <EditCustomerGroupModal
          trigger={
            <Button primary>
              <Icon name="plus" />
              {t(
                'providerAdmin.customerGroupsTable.newCustomerGroupButtonText',
                'New Customer Group'
              )}
            </Button>
          }
          afterSubmit={() => {
            refetch();
          }}
          mode="create"
        />
      </Flex>
      <ErrorMessage error={error} />
      {isPending && <SegmentLoader />}
      {!isPending && res?.data && (
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                {t(
                  'providerAdmin.customerGroupsTable.columnNameAndDesc',
                  'Name & Description'
                )}
              </Table.HeaderCell>
              <Table.HeaderCell width={2}>
                {t('common.actions', 'Actions')}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {!isPending && isEmpty(customerGroups) && (
              <Table.Row>
                <Table.Cell colSpan={3} style={{ textAlign: 'center' }}>
                  {t(
                    'providerAdmin.customerGroupsTable.noCustomerGroups',
                    'No Customer Groups'
                  )}
                </Table.Cell>
              </Table.Row>
            )}
            {customerGroups.map((item, index) => (
              <Table.Row key={index}>
                <Table.Cell>
                  <strong>{item.label}</strong>
                  <br />
                  {item.description}
                </Table.Cell>
                <Table.Cell>
                  <EditCustomerGroupModal
                    trigger={
                      <Button
                        basic
                        icon="pen-to-square"
                        title={t('common.edit', 'Edit')}
                      />
                    }
                    afterSubmit={() => {
                      refetch();
                    }}
                    values={item}
                    mode="edit"
                  />
                  <ConfirmDelete
                    onConfirm={async () => handleDelete(item.id)}
                    name={item.label}
                    objectName={t('common.customerGroup', 'Customer Group')}
                    trigger={
                      <Button
                        basic
                        icon="trash"
                        title={t('common.delete', 'Delete')}
                      />
                    }
                  />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
      <TablePagination
        page={page}
        limit={PAGE_LIMIT}
        total={total}
        onPageChange={(e, data) => {
          const pageNum =
            typeof data.activePage === 'number'
              ? data.activePage
              : Number(data.activePage);
          if (!isNaN(pageNum)) {
            setPage(pageNum);
            refetch();
          }
        }}
      />
    </>
  );
}
